import { LucideIcon, Contact, FolderLock, Headset, LayoutGrid, LogOut, Settings, Handshake, Filter, Calendar, GraduationCap, ListChecks } from "lucide-react";
import {
    PATH_CALL_CENTER,
    PATH_CALL_CENTER_CLOUD_RECORDING_DETAILS,
    PATH_CALL_CENTER_CLOUD_RECORDINGS,
    PATH_CALL_CENTER_JOIN,
    PATH_CALL_CENTER_JOIN_AS_ADMIN,
    PATH_CALL_CENTER_LEAD_RATE,
    PATH_CALL_CENTER_EOD_REPORT,
    PATH_DASHBOARD,
    PATH_PIPELINES,
    PATH_FUNNELS,
    PATH_LEADS,
    PATH_LOGOUT,
    PATH_PRIVACY_POLICY,
    PATH_SETTINGS,
    PATH_SETTINGS_BILLING,
    PATH_SETTINGS_CALL_CENTER,
    PATH_SETTINGS_DOMAIN_CONNECT,
    PATH_SETTINGS_DOMAINS,
    PATH_SETTINGS_FIELDS,
    PATH_SETTINGS_INTEGRATIONS,
    PATH_SETTINGS_INTEGRATIONS_GOOGLE_ADS,
    PATH_SETTINGS_INTEGRATIONS_SLACK,
    PATH_SETTINGS_INTEGRATIONS_HUBSPOT,
    PATH_SETTINGS_INTEGRATIONS_META,
    PATH_SETTINGS_PIXEL_INSTALL,
    PATH_SETTINGS_PROFILE,
    PATH_SETTINGS_USER_MANAGEMENT,
    PATH_TASKS,
    PATH_MEETING_SCHEDULER,
    PATH_MANAGE_MEETING_SCHEDULER,
    PATH_SETTINGS_AVAILABILITY,
    PATH_SETTINGS_INTEGRATIONS_GOOGLE_CALENDAR,
    PATH_CALL_CENTER_COMPANY_ROOM,
    PATH_CALL_CENTER_CALL_LEAD,
    PATH_CALL_CENTER_PERSONAL_ROOM_RECORDINGS,
    PATH_TRAINING,
    PATH_TRAINING_WEEKLY_REPORT,
    PATH_TRAINING_AGENT_PROFILE,
    PATH_SETTINGS_TRAINING,
} from "@routes/paths";
import { TPermissionKey } from "@/utils/permissions";


export interface RouteConfig {
    path: string;
    /**
     * Permission keys (`module:action`) required to enter the route. ANY match grants access.
     * Omit (together with `public`) only if the route should be inaccessible to every non-admin user.
     */
    requiredPermissions?: TPermissionKey[];
    /**
     * When true, the route is accessible to any authenticated user and is always visible in the
     * sidebar — regardless of permissions. Use sparingly for app-level pages (e.g. Dashboard).
     */
    public?: boolean;

    // UI (opcional)
    title?: string;
    icon?: LucideIcon;
    section?: "main" | "settings" | "call-center" | "footer";
}

// Dashboard
export const APP_ROUTES: RouteConfig[] = [
    {
        path: PATH_DASHBOARD,
        // Dashboard is the app's landing page — open to any authenticated user.
        public: true,
        title: "Dashboard",
        icon: LayoutGrid,
        section: "main"
    },

    // Funnels
    {
        path: PATH_FUNNELS,
        requiredPermissions: ["leads:viewAll"],
        title: "Funnels",
        icon: Filter,
        section: "main"
    },

    // Logout
    {
        path: PATH_LOGOUT,
        public: true,
        title: "Log out",
        icon: LogOut,
        section: "footer"
    },

    // Privacy Policy
    {
        path: PATH_PRIVACY_POLICY,
        public: true,
        title: "Privacy Policy",
        icon: FolderLock,
        section: "footer"
    },

    // Settings
    {
        path: PATH_SETTINGS,
        public: true
    },
    {
        path: PATH_SETTINGS_PROFILE,
        public: true,
        title: "Settings",
        icon: Settings,
        section: "footer"
    },
    {
        path: PATH_SETTINGS_USER_MANAGEMENT,
        requiredPermissions: ["users:access"]
    },
    {
        path: PATH_SETTINGS_INTEGRATIONS,
        requiredPermissions: ["company:edit"]
    },
    {
        path: PATH_SETTINGS_BILLING,
        requiredPermissions: ["company:edit"]
    },
    {
        path: PATH_SETTINGS_DOMAINS,
        requiredPermissions: ["company:edit"]
    },
    {
        path: PATH_SETTINGS_PIXEL_INSTALL,
        requiredPermissions: ["company:edit"]
    },
    {
        path: PATH_SETTINGS_DOMAIN_CONNECT,
        requiredPermissions: ["company:edit"]
    },
    {
        path: PATH_SETTINGS_INTEGRATIONS_HUBSPOT,
        requiredPermissions: ["company:edit"]
    },
    {
        path: PATH_SETTINGS_INTEGRATIONS_META,
        requiredPermissions: ["company:edit"]
    },
    {
        path: PATH_SETTINGS_INTEGRATIONS_GOOGLE_ADS,
        requiredPermissions: ["company:edit"]
    },
    {
        path: PATH_SETTINGS_INTEGRATIONS_SLACK,
        requiredPermissions: ["company:edit"]
    },
    {
        path: PATH_SETTINGS_INTEGRATIONS_GOOGLE_CALENDAR,
        requiredPermissions: ["company:edit"]
    },
    {
        path: PATH_SETTINGS_FIELDS,
        requiredPermissions: ["company:edit"]
    },
    {
        path: PATH_SETTINGS_AVAILABILITY,
        public: true
        // Open to any logged-in user — editing own availability.
    },
    {
        path: PATH_SETTINGS_CALL_CENTER,
        requiredPermissions: ["callCenterSettings:access", "callCenterSettings:edit"]
    },
    {
        path: PATH_SETTINGS_TRAINING,
        requiredPermissions: ["company:edit"]
    },

    // Leads
    {
        path: PATH_LEADS,
        requiredPermissions: ["leads:access"],
        title: "Leads",
        icon: Contact,
        section: "main"
    },

    // Pipelines
    {
        path: PATH_PIPELINES,
        requiredPermissions: ["deals:access"],
        title: "Pipelines",
        icon: Handshake,
        section: "main"
    },

    // Call Center
    {
        path: PATH_CALL_CENTER,
        requiredPermissions: ["callCenter:access"],
        title: "Call Center",
        icon: Headset,
        section: "main"
    },
    {
        path: PATH_CALL_CENTER_CALL_LEAD,
        requiredPermissions: ["callCenter:access"]
    },
    // Training
    {
        path: PATH_TRAINING,
        requiredPermissions: ["training:access"],
        title: "Training",
        icon: GraduationCap,
        section: "main"
    },
    {
        path: PATH_TRAINING_WEEKLY_REPORT,
        requiredPermissions: ["training:access"]
    },
    {
        path: PATH_TRAINING_AGENT_PROFILE,
        requiredPermissions: ["training:access"]
    },

    // Tasks
    {
        path: PATH_TASKS,
        public: true,
        title: "Tasks",
        icon: ListChecks,
        section: "main"
    },

    // Meeting Scheduler
    {
        path: PATH_MEETING_SCHEDULER,
        requiredPermissions: ["scheduler:edit"],
        title: "Meeting Scheduler",
        icon: Calendar,
        section: "main"
    },
    {
        path: PATH_MANAGE_MEETING_SCHEDULER,
        requiredPermissions: ["scheduler:access"],
    },
    {
        path: PATH_CALL_CENTER_COMPANY_ROOM,
        requiredPermissions: ["callCenter:access"]
    },
    {
        path: PATH_CALL_CENTER_JOIN,
        requiredPermissions: ["callCenter:access"]
    },
    {
        path: PATH_CALL_CENTER_JOIN_AS_ADMIN,
        requiredPermissions: ["callCenter:joinCallInvisible"]
    },
    {
        path: PATH_CALL_CENTER_LEAD_RATE,
        requiredPermissions: ["callCenter:access"]
    },
    {
        path: PATH_CALL_CENTER_EOD_REPORT,
        requiredPermissions: ["callCenter:access"]
    },
    {
        path: PATH_CALL_CENTER_CLOUD_RECORDINGS,
        requiredPermissions: ["recordings:access"]
    },
    {
        path: PATH_CALL_CENTER_CLOUD_RECORDING_DETAILS,
        requiredPermissions: ["recordings:access"]
    },
    {
        path: PATH_CALL_CENTER_PERSONAL_ROOM_RECORDINGS,
        requiredPermissions: ["recordings:access"]
    },


];
